import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';

import FormErrorBlock from 'src/registry/components/blocks/FormErrorBlock';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';

import { commonMessages, forgotPasswordMessages, loginMessages } from 'src/features/auth/utils/messages';
import { loginFormSchema, LoginPayload, useLogin } from 'src/features/auth/api/useLogin';

const LoginForm = () => {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const {
        mutate: login,
        error,
        isPending,
    } = useLogin({
        onSuccess: () => {
            return navigate('/');
        },
    });

    const form = useForm<LoginPayload>({
        resolver: zodResolver(loginFormSchema),
        defaultValues: {
            username: '',
            password: '',
        },
    });

    const onSubmit = (values: LoginPayload) => {
        login(values);
    };

    const usernameField = form.register('username', { required: true });
    const passwordField = form.register('password', { required: true });

    return (
        <>
            <div className="flex w-full max-w-sm flex-col items-center py-6 text-center">
                <h1 className="text-2xl font-semibold">
                    <FormattedMessage id={loginMessages.welcomeBack.id} defaultMessage={loginMessages.welcomeBack.defaultMessage} /></h1>
                <h3 className="text-center text-gray-500">
                    <FormattedMessage id={loginMessages.loginMessage.id} defaultMessage={loginMessages.loginMessage.defaultMessage} />
                </h3>
            </div>
            <Form form={form} onSubmit={form.handleSubmit(onSubmit)} className="w-full max-w-sm">
                <FormItem>
                    <FormLabel>
                        <FormattedMessage id={commonMessages.username.id} defaultMessage={commonMessages.username.defaultMessage} />
                    </FormLabel>
                    <FormControl>
                        <Input
                            placeholder={formatMessage(commonMessages.usernamePlaceholder)}
                            {...usernameField}
                        />
                    </FormControl>
                    <FormMessage>
                        <FormattedMessage id={loginMessages.loginUsernameTip.id} defaultMessage={loginMessages.loginUsernameTip.defaultMessage} />
                    </FormMessage>
                </FormItem>
                <FormItem>
                    <FormLabel>
                        <FormattedMessage id={commonMessages.password.id} defaultMessage={commonMessages.password.defaultMessage} />
                    </FormLabel>
                    <FormControl>
                        <Input
                            type="password"
                            placeholder="••••••••" {...passwordField}
                        />
                    </FormControl>
                    <FormMessage>
                        <FormattedMessage id={loginMessages.loginPasswordTip.id} defaultMessage={loginMessages.loginPasswordTip.defaultMessage} />
                    </FormMessage>
                </FormItem>
                <Button className="w-full" type="submit" disabled={isPending || !form.formState.isValid}>
                    <FormattedMessage id={loginMessages.login.id} defaultMessage={loginMessages.login.defaultMessage} />
                </Button>
                <FormErrorBlock error={error} />
                <Link
                    to="/forgot-password"
                    className="self-start text-sm text-primary-700 hover:text-primary-600 hover:underline"
                >
                    <FormattedMessage id={forgotPasswordMessages.forgotPassword.id} defaultMessage={forgotPasswordMessages.forgotPassword.defaultMessage} />
                </Link>
            </Form>
        </>
    );
};

export { LoginForm };
